import React, { useEffect, useRef, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';
import { SimulatorModel } from '../../../models/SimulatorModel';
import './TermsAndCoversModal.css';

interface TermsAndCoversModalProps {
    api: SimulatorModel;
    /** persisted route id (the row's `id`) whose terms are edited */
    routeId: string;
    onClose: () => void;
    /** called after a successful save with the saved rich-text content */
    onSaved?: (saved: {
        temsAndCovers: string;
        premiumExpansion: string;
        text: string;
        text2: string;
    }) => void;
}

// TinyMCE (cloud) key already used across the app for the rich-text editors.
const TINYMCE_API_KEY = 'djtel3sjck5h6t7794w5lrm3q570rvj1rxr48es3xq7ft83w';

const editorInit = {
    height: 260,
    menubar: false,
    directionality: 'rtl' as const,
    language: 'he_IL',
    plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
        'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount',
    ],
    toolbar:
        'undo redo | blocks | bold italic underline forecolor | ' +
        'alignleft aligncenter alignright alignjustify | ' +
        'bullist numlist outdent indent | link table | removeformat | code',
    content_style: 'body { font-family:Heebo,Helvetica,Arial,sans-serif; font-size:14px }',
};

/** React port of _TermsAndCovers.cshtml — per-route terms/covers + premium expansion + attached file. */
const TermsAndCoversModal: React.FC<TermsAndCoversModalProps> = ({
    api,
    routeId,
    onClose,
    onSaved,
}) => {
    const termsRef = useRef<TinyMCEEditor | null>(null);
    const premiumRef = useRef<TinyMCEEditor | null>(null);
    const textRef = useRef<TinyMCEEditor | null>(null);
    const text2Ref = useRef<TinyMCEEditor | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const [terms, setTerms] = useState('');
    const [premium, setPremium] = useState('');
    const [text, setText] = useState('');
    const [text2, setText2] = useState('');
    const [filePath, setFilePath] = useState('');
    const [file, setFile] = useState<File | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const route = await api.simulatorShowTermsAndCovers(routeId);
                if (cancelled) return;
                setTerms(route?.temsAndCovers ?? '');
                setPremium(route?.premiumExpansion ?? '');
                setText(route?.text ?? '');
                setText2(route?.text2 ?? '');
                setFilePath(route?.temsAndCoversFilePath ?? '');
            } catch (e: any) {
                if (!cancelled)
                    setError(
                        `טעינת התנאים והכיסויים נכשלה${e?.status ? ` (קוד ${e.status})` : ''}`
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [api, routeId]);

    const save = async () => {
        setError(null);
        setSaving(true);
        try {
            const termsHtml = termsRef.current?.getContent() ?? terms;
            const premiumHtml = premiumRef.current?.getContent() ?? premium;
            const textHtml = textRef.current?.getContent() ?? text;
            const text2Html = text2Ref.current?.getContent() ?? text2;
            await api.simulatorSaveTermsAndCovers(
                routeId,
                termsHtml,
                premiumHtml,
                textHtml,
                text2Html,
                file
            );
            onSaved?.({
                temsAndCovers: termsHtml,
                premiumExpansion: premiumHtml,
                text: textHtml,
                text2: text2Html,
            });
            onClose();
        } catch (e: any) {
            setError(
                `שמירת התנאים והכיסויים נכשלה${e?.status ? ` (קוד ${e.status})` : ''}`
            );
            setSaving(false);
        }
    };

    // basename of an existing attached file path, for display
    const currentFileName = filePath ? filePath.split('/').pop() : '';

    return (
        <div className="sbsim-tc" onClick={onClose}>
            <div
                className="sbsim-tc__dialog"
                dir="rtl"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sbsim-tc__header">
                    <h3 className="sbsim-tc__title">תנאים וכיסויים</h3>
                    <button
                        type="button"
                        className="sbsim-tc__close"
                        aria-label="סגור"
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>

                <div className="sbsim-tc__body">
                    {loading ? (
                        <div className="sbsim-tc__state">טוען...</div>
                    ) : (
                        <>
                            <div className="sbsim-tc__field">
                                <Editor
                                    apiKey={TINYMCE_API_KEY}
                                    onInit={(_evt, editor) => (termsRef.current = editor)}
                                    initialValue={terms}
                                    init={editorInit}
                                />
                            </div>

                            <div className="sbsim-tc__field">
                                <div className="sbsim-tc__label">הרחבות פרמיום</div>
                                <Editor
                                    apiKey={TINYMCE_API_KEY}
                                    onInit={(_evt, editor) => (premiumRef.current = editor)}
                                    initialValue={premium}
                                    init={editorInit}
                                />
                            </div>

                            <div className="sbsim-tc__field">
                                <div className="sbsim-tc__label">הערות</div>
                                <Editor
                                    apiKey={TINYMCE_API_KEY}
                                    onInit={(_evt, editor) => (textRef.current = editor)}
                                    initialValue={text}
                                    init={editorInit}
                                />
                            </div>

                            <div className="sbsim-tc__field">
                                <div className="sbsim-tc__label">הערות להצעת מחיר</div>
                                <Editor
                                    apiKey={TINYMCE_API_KEY}
                                    onInit={(_evt, editor) => (text2Ref.current = editor)}
                                    initialValue={text2}
                                    init={editorInit}
                                />
                            </div>

                            <div className="sbsim-tc__field">
                                <div className="sbsim-tc__label">
                                    קובץ שיצורף לשליחת הצעת המחיר
                                </div>
                                {currentFileName && !file && (
                                    <div className="sbsim-tc__file-current">
                                        קובץ נוכחי: {currentFileName}
                                    </div>
                                )}
                                <input
                                    type="file"
                                    className="sbsim-tc__file"
                                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                                />
                            </div>
                        </>
                    )}

                    {error && <div className="sbsim-tc__error">{error}</div>}
                </div>

                <div className="sbsim-tc__footer">
                    <button
                        type="button"
                        className="sbsim-tc__save"
                        onClick={save}
                        disabled={loading || saving}
                    >
                        {saving ? 'שומר...' : 'שמור'}
                    </button>
                    <button
                        type="button"
                        className="sbsim-tc__cancel"
                        onClick={onClose}
                        disabled={saving}
                    >
                        ביטול
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermsAndCoversModal;
